import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { User, History } from "lucide-react";
import { formatBRL } from "./LoanDetailsDialog";
import { interestShare } from "./LoansSummary";
import LoanRow from "./LoanRow";

export default function CustomerLoansDialog({ customer, loans, today, onClose, onOpenLoan }) {
  const stats = useMemo(() => {
    if (!customer) return null;
    const mine = loans.filter((l) => l.customer_id === customer.id).sort((a, b) => b.start_date.localeCompare(a.start_date));
    const open = mine.filter((l) => l.status !== "paid");
    const paid = mine.filter((l) => l.status === "paid");
    const lent = mine.reduce((s, l) => s + (l.principal || 0), 0);
    const remaining = open.reduce((s, l) => s + Math.max(0, l.total_receivable - l.total_paid), 0);
    const interest = mine.reduce(
      (s, l) => s + (l.payments || []).reduce((a, p) => a + (p.interest_only ? p.amount : p.amount * interestShare(l)), 0),
      0
    );
    return { mine, open, paid, lent, remaining, interest };
  }, [customer, loans]);

  if (!customer || !stats) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-loans-dialog" className="flex max-h-[90vh] flex-col gap-3 overflow-hidden border-white/10 bg-[#121212] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-white">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D4FF00]/30 bg-[#D4FF00]/10 text-[#D4FF00]">
              <User className="h-4 w-4" />
            </span>
            {customer.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {customer.phone ? `${customer.phone} · ` : ""}Histórico completo de empréstimos.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Empréstimos" value={`${stats.mine.length}`} sub={`${stats.open.length} aberto${stats.open.length === 1 ? "" : "s"} · ${stats.paid.length} quitado${stats.paid.length === 1 ? "" : "s"}`} color="#FFFFFF" testid="customer-stat-count" />
          <Stat label="Total emprestado" value={formatBRL(stats.lent)} color="#60A5FA" testid="customer-stat-lent" />
          <Stat label="Em aberto" value={formatBRL(stats.remaining)} color="#F59E0B" testid="customer-stat-remaining" />
          <Stat label="Juros recebidos" value={formatBRL(stats.interest)} color="#A78BFA" testid="customer-stat-interest" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="mb-1.5 flex items-center gap-2 text-[0.7rem] uppercase tracking-widest text-zinc-400">
            <History className="h-3.5 w-3.5 text-[#D4FF00]" />
            Histórico <span className="text-zinc-600">({stats.mine.length})</span>
          </div>
          {stats.mine.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 px-3 py-4 text-xs text-zinc-600">Nenhum empréstimo para este cliente.</div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-white/10 bg-black/20">
              {stats.mine.map((l, i) => (
                <LoanRow key={l.id} loan={l} today={today} showCustomer={false} border={i > 0} compact onClick={() => onOpenLoan(l)} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color, testid, sub }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-white/10 bg-black/30 p-3">
      <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono-num text-base font-bold tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[0.62rem] text-zinc-500">{sub}</div>}
    </div>
  );
}
